import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type CollectionKind } from '../lib/api.ts';
import { qk } from '../lib/queryClient.ts';
import { Icon } from '../lib/icons.tsx';
import { Card } from '../components/Card.tsx';
import { navigate } from '../lib/router.ts';
import { useCategoryNameMap } from '../lib/categoryNames.ts';

const KIND_LABEL: Record<CollectionKind, string> = {
  private: '비공개',
  public: '공개',
  sponsored: '스폰서',
};

type Tab = 'all' | 'collections' | 'export';

interface FavoritesPageProps {
  tab: Tab;
  favs: string[];
  onFav: (id: string) => void;
  onOpen: (id: string) => void;
}

type ExportFormat = 'html' | 'markdown' | 'csv';

const TAB_PATH: Record<Tab, string> = {
  all: '/favorites',
  collections: '/favorites/collections',
  export: '/favorites/export',
};

export function FavoritesPage({ tab, favs, onFav, onOpen }: FavoritesPageProps) {
  const [exportFormat, setExportFormat] = useState<ExportFormat>('html');
  const qc = useQueryClient();

  const meQ = useQuery({ queryKey: qk.auth.me(), queryFn: () => api.me(), retry: false });
  const loggedIn = Boolean(meQ.data?.user);

  const collectionsQ = useQuery({
    queryKey: ['my-collections'],
    queryFn: () => api.listMyCollections(),
    enabled: loggedIn,
  });
  const collections = collectionsQ.data?.items ?? [];

  const [newTitle, setNewTitle] = useState('');
  const [newKind, setNewKind] = useState<CollectionKind>('private');
  const [creating, setCreating] = useState(false);

  const createCollection = async () => {
    const title = newTitle.trim();
    if (!title) return;
    setCreating(true);
    try {
      await api.createCollection({ title, kind: newKind });
      setNewTitle('');
      setNewKind('private');
      qc.invalidateQueries({ queryKey: ['my-collections'] });
    } finally {
      setCreating(false);
    }
  };


  const listQ = useQuery({
    queryKey: qk.services.list({ pageSize: 60, sort: 'newest' }),
    queryFn: () => api.listServices({ pageSize: 60, sort: 'newest' }),
  });
  const services = listQ.data?.items ?? [];
  const favServices = useMemo(() => services.filter((s) => favs.includes(s.slug)), [services, favs]);
  const catMap = useCategoryNameMap();
  const catNameOf = (slug: string | null): string =>
    slug ? catMap.get(slug) ?? slug : '미분류';

  const exportContent = useMemo(() => {
    if (exportFormat === 'html') {
      return `<!DOCTYPE html>\n<html><head><meta charset="utf-8">\n<title>내 newflix 북마크</title></head>\n<body>\n<h1>내가 찜한 서비스 (${favServices.length})</h1>\n<ul>\n${favServices.map((s) => `  <li><a href="#">${s.name}</a> — ${s.tagline} (${catNameOf(s.category)})</li>`).join('\n')}\n</ul>\n</body></html>`;
    }
    if (exportFormat === 'markdown') {
      return `# 내 newflix 북마크 (${favServices.length}개)\n\n${favServices.map((s) => `- **${s.name}** — ${s.tagline}  \n  ${catNameOf(s.category)} · ★ ${s.rating}`).join('\n')}\n`;
    }
    return `name,category,tagline,rating\n${favServices.map((s) => `"${s.name}","${catNameOf(s.category)}","${s.tagline}",${s.rating}`).join('\n')}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportFormat, favServices, catMap]);

  const download = () => {
    const mime =
      exportFormat === 'html' ? 'text/html' : exportFormat === 'markdown' ? 'text/markdown' : 'text/csv';
    const ext = exportFormat === 'markdown' ? 'md' : exportFormat;
    const blob = new Blob([exportContent], { type: `${mime};charset=utf-8` });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newflix-favorites.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <main className="nf-page">
      <div className="nf-fav-head">
        <div>
          <h1>찜한 서비스</h1>
          <div className="sub">하나씩 다시 보거나, 묶어서 내보내거나.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, fontSize: 13, color: 'var(--fg-muted)' }}>
          <span>
            총 <b style={{ color: 'var(--fg)' }}>{favServices.length}</b>개
          </span>
        </div>
      </div>

      <div className="nf-tabs">
        <div className={`nf-tab ${tab === 'all' ? 'active' : ''}`} onClick={() => navigate(TAB_PATH.all)}>
          전체 찜<span className="count">{favServices.length}</span>
        </div>
        <div className={`nf-tab ${tab === 'collections' ? 'active' : ''}`} onClick={() => navigate(TAB_PATH.collections)}>
          컬렉션<span className="count">{collections.length}</span>
        </div>
        <div className={`nf-tab ${tab === 'export' ? 'active' : ''}`} onClick={() => navigate(TAB_PATH.export)}>
          내보내기
        </div>
      </div>

      {tab === 'all' &&
        (listQ.isLoading ? (
          <div style={{ padding: '80px 56px', textAlign: 'center', color: 'var(--fg-muted)' }}>
            불러오는 중…
          </div>
        ) : favServices.length === 0 ? (
          <div style={{ padding: '80px 56px', textAlign: 'center', color: 'var(--fg-muted)' }}>
            아직 찜한 서비스가 없어요.
            <br />
            <a href="/" style={{ color: 'var(--accent)', marginTop: 8, display: 'inline-block' }}>
              둘러보러 가기 →
            </a>
          </div>
        ) : (
          <div className="nf-grid">
            {favServices.map((s) => (
              <div key={s.slug} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Card service={s} isFav onFav={onFav} onOpen={onOpen} />
                {loggedIn && collections.length > 0 && (
                  <select
                    className="nf-coll-kind-select"
                    value=""
                    onChange={async (e) => {
                      const cid = e.target.value;
                      if (!cid) return;
                      await api.addServiceToCollection(cid, s.slug);
                      e.target.value = '';
                    }}
                    title="컬렉션에 담기"
                  >
                    <option value="">+ 컬렉션에 담기</option>
                    {collections.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.title}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            ))}
          </div>
        ))}

      {tab === 'collections' && (
        <div style={{ padding: '0 56px' }}>
          {!loggedIn ? (
            <div style={{ padding: '60px 0', textAlign: 'center', color: 'var(--fg-muted)' }}>
              컬렉션은 로그인 후 만들 수 있어요.
              <br />
              <a href="/me" style={{ color: 'var(--accent)', marginTop: 8, display: 'inline-block' }}>
                로그인하러 가기 →
              </a>
            </div>
          ) : (
            <>
              <div className="nf-coll-create">
                <input
                  className="nf-modal-input"
                  style={{ flex: 1 }}
                  placeholder="새 컬렉션 이름"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void createCollection();
                  }}
                  maxLength={120}
                />
                <select
                  className="nf-coll-kind-select"
                  value={newKind}
                  onChange={(e) => setNewKind(e.target.value as CollectionKind)}
                >
                  <option value="private">비공개</option>
                  <option value="public">공개</option>
                  <option value="sponsored">스폰서</option>
                </select>
                <button
                  className="nf-btn accent"
                  style={{ height: 42 }}
                  disabled={creating || !newTitle.trim()}
                  onClick={createCollection}
                >
                  {creating ? '만드는 중…' : '만들기'}
                </button>
              </div>

              {collections.length === 0 ? (
                <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--fg-muted)' }}>
                  아직 컬렉션이 없어요. 위에서 첫 컬렉션을 만들어보세요.
                </div>
              ) : (
                <div className="nf-coll-list">
                  {collections.map((c) => (
                    <div key={c.id} className="nf-coll-item">
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 700, fontSize: 15 }}>{c.title}</span>
                          <span className={`nf-coll-badge ${c.kind}`}>{KIND_LABEL[c.kind]}</span>
                        </div>
                        {c.description && (
                          <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 2 }}>
                            {c.description}
                          </div>
                        )}
                        <code style={{ fontSize: 11, color: 'var(--fg-dim)' }}>/col/{c.publicCode}</code>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <button
                          className="nf-btn ghost"
                          style={{ height: 34, padding: '0 12px', fontSize: 12 }}
                          onClick={() => navigate('/col/' + c.publicCode)}
                        >
                          관리
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === 'export' && (
        <>
          <div className="nf-export-grid">
            <div
              className={`nf-export-card ${exportFormat === 'html' ? 'active' : ''}`}
              onClick={() => setExportFormat('html')}
            >
              <div className="ico">HTML</div>
              <h4>북마크 HTML</h4>
              <p>크롬·사파리에 그대로 임포트할 수 있는 표준 북마크 파일.</p>
            </div>
            <div
              className={`nf-export-card ${exportFormat === 'markdown' ? 'active' : ''}`}
              onClick={() => setExportFormat('markdown')}
            >
              <div className="ico">MD</div>
              <h4>Markdown</h4>
              <p>Notion, Obsidian, GitHub Gist에 붙여넣기.</p>
            </div>
            <div
              className={`nf-export-card ${exportFormat === 'csv' ? 'active' : ''}`}
              onClick={() => setExportFormat('csv')}
            >
              <div className="ico">CSV</div>
              <h4>CSV</h4>
              <p>구글 시트·엑셀에서 분석하고 싶을 때.</p>
            </div>
          </div>
          <div
            style={{
              padding: '0 56px 14px',
              maxWidth: 1400,
              margin: '0 auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ fontSize: 13, color: 'var(--fg-muted)' }}>
              {favServices.length}개 서비스 · {exportFormat.toUpperCase()} 형식
            </div>
            <button className="nf-btn accent" style={{ height: 40, padding: '0 18px' }} onClick={download}>
              다운로드
            </button>
          </div>
          <div className="nf-export-preview">{exportContent}</div>
        </>
      )}
    </main>
  );
}
